import React, { useRef, useState } from 'react'
import { Form, Button, Modal, Card, Spinner, Badge, Alert, Row, Col } from 'react-bootstrap'
import { FaTimes } from 'react-icons/fa'
import CreatableSelect from 'react-select/creatable'
import PasswordSection from './password-section'
import { useAuth } from '../../auth/auth-context'
import useFetch from '../../_fetch'
import { currencies } from '../../_utils'

function stripCommonSubdomain(hostname: string) {
    return hostname.replace(/^www\d*\./i, '')
}

function cleanDomain(input: string) {
    try {
        const value = input.trim()
        const url = value.startsWith('http') ? value : `https://${value}`
        const parsed = new URL(url)
        return stripCommonSubdomain(parsed.hostname.toLowerCase())
    } catch {
        return null
    }
}

function isValidDomain(domain: string) {
    return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)
}

export default function UserSettings() {
    const { user, login } = useAuth()
    const { doFetch } = useFetch()
    const currency = user?.settings?.currency
    const companySettings = user?.settings?.company

    const [formData, setFormData] = useState({
        currency: currency || 'EUR',
        companyName: companySettings?.name || user?.companyName || '',
        companyContactEmail: companySettings?.contactEmail || user?.email || '',
        companyVatNumber: companySettings?.vatNumber || '',
        companyDomains: Array.isArray(companySettings?.domains) ? companySettings?.domains : []
    })
    const [submitting, setSubmitting] = useState(false)
    const [domainError, setDomainError] = useState('')
    const [companyNameError, setCompanyNameError] = useState('')
    const [companyDomainsError, setCompanyDomainsError] = useState('')

    const [successModalMsg, setSuccessModalMsg] = useState('')
    const [errorModalMsg, setErrorModalMsg] = useState('')
    const domainsSelectRef = useRef<any>(null)
    const actionError = domainError || companyNameError || companyDomainsError
    const isDomainFieldInvalid = !!(domainError || companyDomainsError)

    const domainSelectStyles = {
        control: (base: any, state: any) => ({
            ...base,
            borderColor: isDomainFieldInvalid ? '#dc3545' : base.borderColor,
            boxShadow: isDomainFieldInvalid
                ? '0 0 0 0.2rem rgba(220,53,69,0.25)'
                : state.isFocused
                    ? base.boxShadow
                    : 'none',
            '&:hover': {
                ...base['&:hover'],
                borderColor: isDomainFieldInvalid ? '#dc3545' : base.borderColor,
            },
        }),
    }

    const handleInputChange = (e: React.ChangeEvent<any>) => {
        const { name, value } = e.target

        if (name === 'companyName') {
            setCompanyNameError('')
        }

        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }))
    }

    const addDomain = (option: any) => {
        const raw = option?.value?.trim()?.toLowerCase()
        if (!raw) return

        const domain = cleanDomain(raw)
        if (!domain || !isValidDomain(domain)) {
            setDomainError('Invalid domain')
            domainsSelectRef.current?.focus()
            return
        }

        setDomainError('')
        setCompanyDomainsError('')
        setFormData((prevData) => ({
            ...prevData,
            companyDomains: (prevData.companyDomains || []).includes(domain)
                ? prevData.companyDomains
                : [...(prevData.companyDomains || []), domain]
        }))
    }

    const removeDomain = (domainToRemove: string) => {
        setFormData((prevData) => ({
            ...prevData,
            companyDomains: (prevData.companyDomains || []).filter((domain) => domain !== domainToRemove)
        }))
    }

    const onSettingsSubmit = async (e:React.FormEvent) => {
        e.preventDefault()

        setDomainError('')
        setCompanyNameError('')
        setCompanyDomainsError('')

        if (!formData.companyName.trim()) {
            setCompanyNameError('Company name is required')
            return
        }

        if (!(formData.companyDomains || []).length) {
            setCompanyDomainsError('At least one website domain is required')
            domainsSelectRef.current?.focus()
            return
        }

        setSubmitting(true)

        try {
            const res = await doFetch('/update-settings', {
                currency: formData.currency,
                companyName: formData.companyName,
                companyContactEmail: formData.companyContactEmail,
                companyVatNumber: formData.companyVatNumber,
                companyDomains: formData.companyDomains
            })
            
            if(res.status >= 400) throw await res.text()

            const {token} = await res.json()

            login(token)
            setSuccessModalMsg('Settings updated successfully')
        } catch (error) {
            setErrorModalMsg('Failed to update settings')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="mt-3 mx-3">
            <h2>User Settings</h2>
            <hr />
            <Card className="mb-3">
                <Card.Header>
                    <div className="fw-semibold">User Information</div>
                    <div className="text-muted small">Basic account identity and access scope.</div>
                </Card.Header>
                <Card.Body>
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="text-muted small mb-1">Username</div>
                            <div className="fw-semibold">{user?.username || '-'}</div>
                        </Col>
                        <Col md={6}>
                            <div className="text-muted small mb-1">User type</div>
                            <div className="fw-semibold text-capitalize">{user?.kind || '-'}</div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Form onSubmit={onSettingsSubmit}>
                <Card className="mb-3">
                    <Card.Header>
                        <div className="fw-semibold">Settings</div>
                        <div className="text-muted small">Manage user preferences and company profile.</div>
                    </Card.Header>
                    <Card.Body>
                        <Row className="g-4">
                            <Col lg={6}>
                                <h6 className="mb-3">Preferences</h6>
                                <Form.Group controlId="formCurrency" className="mb-0">
                                    <Form.Label>Currency</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="currency"
                                        value={formData.currency}
                                        onChange={handleInputChange}
                                    >
                                        {currencies.map((currency) => (
                                            <option key={currency} value={currency}>
                                                {currency}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        </Row>

                        <h6 className="mb-3 mt-4">Company</h6>
                        <Row className="g-4">
                            <Col lg={6}>
                                <Form.Group className="mb-0" controlId="formCompanyName">
                                    <Form.Label>Company name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleInputChange}
                                        placeholder="Company name"
                                        isInvalid={!!companyNameError}
                                    />
                                </Form.Group>
                            </Col>

                            <Col lg={6}>
                                <Form.Group className="mb-0" controlId="formCompanyDomains">
                                    <Form.Label>Website domains</Form.Label>
                                    <CreatableSelect
                                        ref={domainsSelectRef}
                                        placeholder="Add domain (e.g. partner.com)"
                                        isClearable
                                        styles={domainSelectStyles}
                                        formatCreateLabel={(value) => `Add \"${value}\"`}
                                        onChange={addDomain}
                                        onCreateOption={(value) => addDomain({ value })}
                                    />
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {(formData.companyDomains || []).map((domain) => (
                                            <Badge key={domain} bg="info" className="d-flex align-items-center px-2 py-1">
                                                {domain}
                                                <FaTimes
                                                    className="ms-2"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => removeDomain(domain)}
                                                />
                                            </Badge>
                                        ))}
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col lg={6}>
                                <Form.Group className="mb-0" controlId="formCompanyVat">
                                    <Form.Label>VAT / CVR (optional)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="companyVatNumber"
                                        value={formData.companyVatNumber}
                                        onChange={handleInputChange}
                                        placeholder="VAT or registration number"
                                    />
                                </Form.Group>
                            </Col>

                            <Col lg={6}>
                                <Form.Group className="mb-0" controlId="formCompanyEmail">
                                    <Form.Label>Contact email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="companyContactEmail"
                                        value={formData.companyContactEmail}
                                        onChange={handleInputChange}
                                        placeholder="name@company.com"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Card.Body>
                    <Card.Footer className="d-flex align-items-center justify-content-between bg-light border-top gap-3">
                        <div className="text-danger small" style={{ minHeight: '1.25rem' }} aria-live="polite">
                            {actionError || ''}
                        </div>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            ) : (
                                'Save Settings'
                            )}
                        </Button>
                    </Card.Footer>
                </Card>
            </Form>

            <PasswordSection setSuccessModalMsg={setSuccessModalMsg} />

            <Modal show={!!successModalMsg} onHide={() => setSuccessModalMsg('')}>
                <Modal.Header closeButton className="bg-success text-white">
                    <Modal.Title>Success</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{successModalMsg}</p>
                </Modal.Body>
            </Modal>

            <Modal show={!!errorModalMsg} onHide={() => setErrorModalMsg('')}>
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{errorModalMsg}</p>
                </Modal.Body>
            </Modal>
        </div>
    )
}
